<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  DEFAULT_SUPPORT_ACCESS_DURATION,
  SUPPORT_ACCESS_DURATIONS,
  supportAccessApi,
  supportAccessErrorMessage,
  type SupportAccessRequest,
} from '@/api/support-access.api'

/**
 * Email access: "View as" always works, but without a grant it opens the support view, where the backend hides
 * everything from Gmail/Outlook (Google's Limited Use rules: staff may only read a user's email with their
 * agreement). To see email, staff file a time-boxed request here; the customer answers it live in the app, and
 * while the grant runs, "View as" opens in full mode. While a request is pending this polls every few seconds,
 * and while a grant runs it re-checks so a revoke shows up without a reload.
 */
const props = defineProps<{
  userId: string
  customerName: string
}>()

const emit = defineEmits<{ 'grant-change': [grant: SupportAccessRequest | null] }>()

const PENDING_POLL_MS = 4000
const GRANT_POLL_MS = 30000

const pending = ref<SupportAccessRequest | null>(null)
const grant = ref<SupportAccessRequest | null>(null)
const loading = ref(false)
const error = ref('')
const outcome = ref('')
const reason = ref('')
const duration = ref<number>(DEFAULT_SUPPORT_ACCESS_DURATION)
const submitting = ref(false)
const now = ref(Date.now())
/** The request form is tucked behind a button: most support work doesn't need email. */
const formOpen = ref(false)

let pollTimer: ReturnType<typeof setTimeout> | null = null
let clockTimer: ReturnType<typeof setInterval> | null = null
let loadSeq = 0

function ts(value: string | null | undefined): number | null {
  if (!value) return null
  const t = new Date(value).getTime()
  return Number.isNaN(t) ? null : t
}

function formatTime(value: string | null | undefined): string {
  const t = ts(value)
  if (t == null) return '—'
  return new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function minutesLeft(value: string | null | undefined): number {
  const t = ts(value)
  if (t == null) return 0
  return Math.max(0, Math.ceil((t - now.value) / 60000))
}

function durationLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} minutes`
  const hours = minutes / 60
  return hours === 1 ? '1 hour' : `${hours} hours`
}

const grantMinutesLeft = computed(() => minutesLeft(grant.value?.granted_until))
const pendingMinutesLeft = computed(() => minutesLeft(pending.value?.request_expires_at))

function clearPoll() {
  if (pollTimer) clearTimeout(pollTimer)
  pollTimer = null
}

function schedulePoll() {
  clearPoll()
  const delay = pending.value ? PENDING_POLL_MS : grant.value ? GRANT_POLL_MS : null
  if (delay != null) pollTimer = setTimeout(() => void load({ quiet: true }), delay)
}

async function load(options: { quiet?: boolean } = {}) {
  const seq = ++loadSeq
  const hadPending = pending.value
  const hadGrant = grant.value
  if (!options.quiet) loading.value = true
  try {
    const res = await supportAccessApi.get(props.userId)
    if (seq !== loadSeq) return
    if (res.status !== 200 || !res.data) {
      if (!options.quiet) error.value = supportAccessErrorMessage(res.data, 'Failed to load support access')
      return
    }
    error.value = ''
    pending.value = res.data.pending
    grant.value = res.data.active_grant
    if (grant.value) {
      outcome.value = ''
    } else if (hadPending && !pending.value) {
      outcome.value = `${props.customerName} didn't allow email access (denied, or the request expired).`
    } else if (hadGrant) {
      outcome.value = 'Email access has ended (it expired or the customer revoked it).'
    }
  } catch (e: unknown) {
    if (seq === loadSeq && !options.quiet) {
      error.value = e instanceof Error ? e.message : 'Failed to load support access'
    }
  } finally {
    if (seq === loadSeq) {
      loading.value = false
      schedulePoll()
    }
  }
}

async function requestAccess() {
  const cleaned = reason.value.trim()
  if (!cleaned || submitting.value) return
  submitting.value = true
  error.value = ''
  outcome.value = ''
  try {
    const res = await supportAccessApi.request(props.userId, cleaned, duration.value)
    if (res.status !== 200 || !res.data) {
      error.value = supportAccessErrorMessage(res.data, 'Failed to send the request')
      return
    }
    pending.value = res.data.status === 'pending' ? res.data : null
    grant.value = res.data.status === 'approved' ? res.data : grant.value
    reason.value = ''
    formOpen.value = false
    schedulePoll()
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : 'Failed to send the request'
  } finally {
    submitting.value = false
  }
}

watch(grant, (value) => emit('grant-change', value), { immediate: true })

// The grant or the request runs out on our clock: re-check rather than wait for the next poll.
watch([grantMinutesLeft, pendingMinutesLeft], ([g, p]) => {
  if ((grant.value && g === 0) || (pending.value && p === 0)) void load({ quiet: true })
})

watch(
  () => props.userId,
  () => {
    clearPoll()
    pending.value = null
    grant.value = null
    error.value = ''
    outcome.value = ''
    reason.value = ''
    duration.value = DEFAULT_SUPPORT_ACCESS_DURATION
    formOpen.value = false
    void load()
  },
)

onMounted(() => {
  clockTimer = setInterval(() => {
    now.value = Date.now()
  }, 15000)
  void load()
})

onBeforeUnmount(() => {
  loadSeq++
  clearPoll()
  if (clockTimer) clearInterval(clockTimer)
})
</script>

<template>
  <section class="rounded-lg border border-gray-200 p-3">
    <div class="flex items-baseline justify-between gap-3">
      <h3 class="text-xs font-medium uppercase tracking-wide text-gray-400">Email access</h3>
      <span v-if="loading" class="text-xs text-gray-400">Checking…</span>
    </div>

    <div v-if="grant" class="mt-2 rounded-md bg-forest/5 px-3 py-2">
      <p class="text-sm font-medium text-forest">
        Email access until {{ formatTime(grant.granted_until) }}
        <span class="font-normal text-gray-500">· {{ grantMinutesLeft }} min left</span>
      </p>
      <p v-if="grant.reason" class="mt-0.5 text-xs text-gray-600">{{ grant.reason }}</p>
      <p v-if="grant.requested_by_name" class="mt-0.5 text-xs text-gray-400">
        Requested by {{ grant.requested_by_name }}
      </p>
    </div>

    <div v-else-if="pending" class="mt-2 rounded-md bg-amber-50 px-3 py-2">
      <p class="flex items-center gap-2 text-sm font-medium text-amber-800">
        <span class="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-500" aria-hidden="true" />
        Waiting for {{ customerName }} to allow email access…
      </p>
      <p class="mt-0.5 text-xs text-amber-700">
        {{ durationLabel(pending.duration_minutes) }} requested · the request expires at
        {{ formatTime(pending.request_expires_at) }}
      </p>
      <p v-if="pending.reason" class="mt-0.5 text-xs text-gray-600">{{ pending.reason }}</p>
      <p class="mt-1 text-xs text-gray-500">
        They see it as a prompt in the Lighthouse app (and get a push notification on their phone).
      </p>
    </div>

    <div v-else-if="!formOpen" class="mt-2 flex items-center justify-between gap-3">
      <p class="text-xs text-gray-500">Email is hidden unless {{ customerName }} allows it.</p>
      <button
        type="button"
        class="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        @click="formOpen = true"
      >
        Request email access
      </button>
    </div>

    <form v-else class="mt-2 space-y-2" @submit.prevent="requestAccess">
      <p class="text-xs text-gray-500">
        {{ customerName }} gets a prompt in the app with your name, the reason and the duration.
      </p>
      <textarea
        v-model="reason"
        rows="2"
        maxlength="1000"
        placeholder="Reason, shown to the customer (required)"
        class="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest"
      />
      <div class="flex items-center gap-2">
        <select
          v-model.number="duration"
          aria-label="Access duration"
          class="rounded-lg border border-gray-200 bg-white py-1.5 pl-3 pr-8 text-xs text-gray-700 focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest"
        >
          <option v-for="minutes in SUPPORT_ACCESS_DURATIONS" :key="minutes" :value="minutes">
            {{ durationLabel(minutes) }}
          </option>
        </select>
        <button type="button" class="ml-auto text-xs text-gray-500 hover:text-gray-700" @click="formOpen = false">
          Cancel
        </button>
        <button
          type="submit"
          class="shrink-0 rounded-lg bg-forest px-3 py-1.5 text-xs font-medium text-white hover:bg-forest/90 disabled:opacity-50"
          :disabled="submitting || !reason.trim()"
        >
          {{ submitting ? 'Sending…' : 'Send request' }}
        </button>
      </div>
    </form>

    <p v-if="outcome" class="mt-2 text-xs text-gray-600">{{ outcome }}</p>
    <p v-if="error" class="mt-2 text-xs text-red-600">{{ error }}</p>
  </section>
</template>
